import { Link, createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { BlueprintButton, BlueprintEmpty, BlueprintGrid, BlueprintLoading, BpFrame, CoordLabel, DesignPlate, InteractiveBlueprintCanvas } from "@/components/blueprint/Blueprint";
import { AppScreenshotToggle } from "@/components/shared/AppCapturePlan";
import { MiniEditorPreview } from "@/components/shared/MiniEditorPreview";
import { QuuilPhoneMedia } from "@/components/shared/QuuilPhoneMedia";
import { SEOHead } from "@/components/shared/SEOHead";
import { fetchCommunityDesigns } from "@/lib/api";
import { logoUrl } from "@/lib/assets";
import { getDesignPreviewUrl, prefetchImages } from "@/lib/imagePrefetch";
import { IS_SPLASH_MODE } from "@/lib/siteMode";
import { APP_STORE_URL, formatNumber } from "@/lib/utils";
import type { CommunityDesignItem } from "@/types/api";

export const Route = createFileRoute("/")({
  component: HomePage,
});

const ProductShowcase = lazy(() =>
  import("@/components/shared/ProductShowcase").then((module) => ({
    default: module.ProductShowcase,
  }))
);

const showcasePreview: Array<{
  label: string;
  lightSrc?: string;
  darkSrc?: string;
  alt: string;
  showModeToggle?: boolean;
}> = [
  {
    label: "Create",
    lightSrc: "/clean-screens/createlight.png",
    darkSrc: "/clean-screens/createdark.png",
    alt: "Quuil home screen",
  },
  {
    label: "Ask Q",
    lightSrc: "/clean-screens/askqlight.png",
    darkSrc: "/clean-screens/askqdark.png",
    alt: "Quuil guided design chat screen",
  },
  {
    label: "Choose",
    lightSrc: "/clean-screens/choosedark.png",
    darkSrc: "/clean-screens/choosedark.png",
    alt: "Quuil product picker screen",
    showModeToggle: false,
  },
];

const platformCapabilities = [
  {
    label: "Create",
    title: "Start Anywhere",
    body: "Snap a photo while you are out with friends, upload one from your camera roll, describe an idea, sketch a direction, or start from Inkwell art.",
  },
  {
    label: "Edit",
    title: "Design Tools",
    body: "Remove backgrounds, apply style effects, crop, rotate, scale, adjust, and compare variations before anything goes on a product.",
  },
  {
    label: "Customize",
    title: "Mockup Editor",
    body: "Put the transformed design on tees, hoodies, hats, mugs, drinkware, stickers, and more with smart print areas and real-time previews.",
  },
  {
    label: "Order",
    title: "Built-In Checkout",
    body: "Add products to cart, apply coupon codes, choose shipping, check out securely, and track the order from account history.",
  },
  {
    label: "Discover",
    title: "The Inkwell",
    body: "Browse community designs for gifts, trips, parties, clubs, small drops, and inside jokes, then use shared work as a launch point.",
  },
  {
    label: "Account",
    title: "Credits & History",
    body: "Sign in with Apple or Google, start in guest mode, manage credits, and keep past designs and orders ready to revisit.",
  },
];

function HomePage() {
  return IS_SPLASH_MODE ? <SplashHomePage /> : <FullHomePage />;
}

function FullHomePage() {
  const [designs, setDesigns] = useState<CommunityDesignItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showcaseDesign, setShowcaseDesign] = useState<CommunityDesignItem | null>(null);
  const [featuredDesignIndex, setFeaturedDesignIndex] = useState(0);

  useEffect(() => {
    fetchCommunityDesigns({ sort: "trending", limit: 8 })
      .then((response) => {
        setDesigns(response.designs);
        prefetchImages(response.designs.map(getDesignPreviewUrl), 10);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load the Inkwell");
      })
      .finally(() => setLoading(false));
  }, []);

  const totalSaves = useMemo(
    () => designs.reduce((sum, design) => sum + (design.save_count || 0), 0),
    [designs]
  );
  const featuredDesigns = useMemo(
    () => designs.filter((design) => getDesignPreviewUrl(design)),
    [designs]
  );
  const featuredDesign = featuredDesigns[featuredDesignIndex % Math.max(featuredDesigns.length, 1)];

  useEffect(() => {
    setFeaturedDesignIndex(0);
  }, [featuredDesigns.length]);

  useEffect(() => {
    if (featuredDesigns.length <= 1) return;
    const timer = window.setInterval(() => {
      setFeaturedDesignIndex((index) => (index + 1) % featuredDesigns.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [featuredDesigns.length]);

  return (
    <div className="bp-page page-enter">
      <SEOHead
        title="Quuil — Design, Customize, Order, and Share Custom Merch"
        description="Create custom artwork, preview it on real products, order securely, and discover community designs in the Quuil app."
        path="/"
        keywords={[
          "custom merch",
          "custom apparel",
          "merch design app",
          "print on demand",
          "community art",
          "custom t-shirts",
        ]}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Quuil",
            url: "https://quuil.com",
            logo: "https://quuil.com/logo.png?v=4",
            sameAs: [
              "https://www.facebook.com/profile.php?id=61589081957671",
              "https://www.instagram.com/quuil_app/",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            name: "Quuil",
            applicationCategory: "DesignApplication",
            operatingSystem: "iOS",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            url: "https://quuil.com",
          },
        ]}
      />

      <section className="bp-hero">
        <BlueprintGrid />
        <div className="bp-noise" aria-hidden="true" />
        <InteractiveBlueprintCanvas />
        <div className="bp-hero-border" aria-hidden="true">
          <div className="bp-title-block">
            <span>Project</span>
            <strong>Quuil Creative Studio</strong>
            <small>ART TO PRODUCT // SCALE 1:1</small>
          </div>
          <CoordLabel className="bp-border-a1">A1</CoordLabel>
          <CoordLabel className="bp-border-a8">A8</CoordLabel>
          <CoordLabel className="bp-border-h1">H1</CoordLabel>
          <CoordLabel className="bp-border-h8">H8</CoordLabel>
        </div>
        <div className="bp-container bp-hero-lockup px-5">
          <div>
            <span className="bp-kicker">Create // Customize // Order // Share</span>
            <img src={logoUrl} alt="Quuil" width={760} height={220} className="bp-hero-logo mx-auto" />
            <div className="bp-app-icon-lockup" aria-label="Quuil app identity">
              <img src="/quuil-app-icon-showcase.png?v=5" alt="Quuil app icon" width={72} height={72} />
              <span>Create · Customize · Order</span>
            </div>
            <p className="bp-subtitle">
              QUUIL is a conversational design partner for real-life moments: snap a photo with friends, transform it into artwork, put it on a tee, hoodie, mug, hat, sticker, or bottle, and order it from your phone.
            </p>
            <div className="bp-hero-actions bp-hero-actions-single">
              <BlueprintButton href={APP_STORE_URL}>Get the App</BlueprintButton>
            </div>
            <div className="bp-credit-offer" aria-label="Download Quuil and get 10 free credits for two design sessions">
              <span>Download Bonus</span>
              <strong>10 free credits</strong>
              <small>for 2 design sessions</small>
            </div>
          </div>
          <div className="bp-click-hint">Click anywhere to leave a mark</div>
        </div>
      </section>

      <section className="bp-section bp-app-showcase">
        <BlueprintGrid light />
        <div className="bp-container bp-app-showcase-grid">
          <div className="bp-section-title mb-0">
            <span className="bp-kicker">02 // Conversational Design Partner</span>
            <h2>Chat, Shape, And Make It Real</h2>
            <p>
              Tell QUUIL what happened, who it is for, and what vibe you want. It helps turn the photo, sketch, prompt, or inside joke into artwork you can clean up, remix, and place on products.
            </p>
            <div className="bp-showcase-tags" aria-label="App highlights">
              <span>Photo Upload</span>
              <span>Live Capture</span>
              <span>Background Removal</span>
              <span>Design Variations</span>
              <span>Image Editor</span>
            </div>
            <div className="bp-hero-actions justify-start">
              <BlueprintButton href={APP_STORE_URL}>Get the App</BlueprintButton>
              <BlueprintButton href="/create" variant="ghost">Create</BlueprintButton>
            </div>
            <p className="bp-inline-credit-note">
              Download bonus <strong>10 free credits</strong> for 2 design sessions.
            </p>
          </div>

          <div className="bp-phone-stage" aria-label="Quuil app preview">
            <span className="bp-phone-glow bp-phone-glow-gold" aria-hidden="true" />
            <span className="bp-phone-glow bp-phone-glow-cyan" aria-hidden="true" />
            <BpFrame className="bp-phone-callout bp-phone-callout-top" label="APP PREVIEW">
              07.24S LOOP
            </BpFrame>
            <QuuilPhoneMedia className="bp-phone-gif" />
          </div>
        </div>
      </section>

      <section className="bp-section bp-product-tour">
        <BlueprintGrid light />
        <div className="bp-container bp-showcase-teaser">
          <div>
            <div className="bp-section-title mb-0">
              <span className="bp-kicker">Mockup Editor</span>
              <h2>Customize The Product Before You Buy</h2>
            <p>
                Make matching shirts for a trip, a mug from a ridiculous photo, hats for a team, stickers for a party, or a small creator drop. Drag, scale, and position the design while Quuil keeps print areas clear.
              </p>
            </div>
            <div className="bp-hero-actions justify-start">
              <BlueprintButton href="/showcase">View Showcase</BlueprintButton>
            </div>
          </div>

          <div className="bp-showcase-strip-wrap">
            <div className="bp-showcase-strip" aria-label="Quuil app preview moments">
              {showcasePreview.map((item) => (
                <div className="bp-showcase-strip-item" key={item.label}>
                  <AppScreenshotToggle
                    title={item.label}
                    lightSrc={item.lightSrc}
                    darkSrc={item.darkSrc}
                    alt={item.alt}
                    priority="screenshot"
                    className="bp-strip-screenshot-toggle"
                    imageClassName="bp-strip-screenshot"
                    placeholderClassName="bp-strip-placeholder"
                    showModeToggle={item.showModeToggle ?? true}
                  />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bp-section bp-capabilities-section">
        <BlueprintGrid light />
        <div className="bp-container">
          <div className="bp-section-title">
            <span className="bp-kicker">Create // Customize // Order // Share</span>
            <h2>Everything You Need To Make Real Merch</h2>
            <p>
              QUUIL is built for specific uses, not vague “make art” moments: gifts, friend trips, events, teams, clubs, inside jokes, creator drops, and community remixes, all connected to print areas, checkout, fulfillment, tracking, saved history, credits, and discovery.
            </p>
            <div className="bp-flow-rail" aria-label="Quuil flow">
              <span>Create</span>
              <span>Customize</span>
              <span>Order</span>
              <span>Share</span>
            </div>
          </div>

          <div className="bp-capability-grid">
            {platformCapabilities.map((capability) => (
              <BpFrame key={capability.title} className="bp-capability-card" label={capability.label}>
                <h3>{capability.title}</h3>
                <p>{capability.body}</p>
              </BpFrame>
            ))}
          </div>
        </div>
      </section>

      <section className="bp-section">
        <BlueprintGrid light />
        <div className="bp-container">
          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div className="bp-section-title mb-0">
              <span className="bp-kicker">The Inkwell</span>
              <h2>Fresh Designs</h2>
              <p>Discover trending and new artwork, save designs you love, and use community work as the starting point for your own mockup.</p>
            </div>
            <Link to="/gallery" className="bp-button bp-button-ghost w-fit">
              View Full Gallery
            </Link>
          </div>

          <div className="mt-8">
            {loading ? (
              <BlueprintLoading />
            ) : error ? (
              <BlueprintEmpty title="Inkwell Offline" detail={error} />
            ) : designs.length === 0 ? (
              <BlueprintEmpty title="No Designs Yet" detail="The Inkwell is quiet right now." />
            ) : (
              <div className="bp-featured">
                <BpFrame className="bp-featured-preview" label="LIVE PREVIEW">
                  <div className="bp-featured-preview-canvas">
                    {featuredDesign ? (
                      <MiniEditorPreview
                        key={featuredDesign.id}
                        imageUrl={getDesignPreviewUrl(featuredDesign)}
                        alt={featuredDesign.title || "Featured Inkwell design"}
                      />
                    ) : null}
                  </div>
                </BpFrame>
                <div className="bp-design-grid">
                  {designs.slice(0, 8).map((design) => (
                    <DesignPlate key={design.id} design={design} onSelect={() => setShowcaseDesign(design)} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 grid gap-3 font-mono text-xs uppercase tracking-[0.12em] text-[var(--bp-dim)] md:grid-cols-3">
            <BpFrame className="p-4" label="ARTWORK">
              {formatNumber(designs.length)} designs loaded
            </BpFrame>
            <BpFrame className="p-4" label="SAVES">
              {formatNumber(totalSaves)} community saves
            </BpFrame>
            <BpFrame className="p-4" label="PRODUCTS">
              tees, hoodies, hats, mugs, drinkware
            </BpFrame>
          </div>
        </div>
      </section>

      <section className="bp-section bp-closing-section">
        <BlueprintGrid light />
        <div className="bp-container text-center">
          <span className="bp-kicker">Ready</span>
          <h2 className="bp-closing-title">
            Make <span>Yours</span>
          </h2>
          <p className="bp-subtitle">
            Start in guest mode, use your free credits, create a design, place it on real products, and check out securely when it is ready.
          </p>
          <div className="bp-hero-actions">
            <BlueprintButton href={APP_STORE_URL}>Open App Store</BlueprintButton>
            <BlueprintButton href="/create" variant="ghost">Start a Design</BlueprintButton>
          </div>
        </div>
      </section>

      {showcaseDesign ? (
        <Suspense fallback={null}>
          <ProductShowcase design={showcaseDesign} onClose={() => setShowcaseDesign(null)} />
        </Suspense>
      ) : null}
    </div>
  );
}

function SplashHomePage() {
  const [inkwellDesigns, setInkwellDesigns] = useState<CommunityDesignItem[]>([]);

  useEffect(() => {
    fetchCommunityDesigns({ sort: "trending", limit: 10 })
      .then((response) => setInkwellDesigns(response.designs))
      .catch(() => setInkwellDesigns([]));
  }, []);

  useEffect(() => {
    prefetchImages(inkwellDesigns.map(getDesignPreviewUrl), 10);
  }, [inkwellDesigns]);

  const marqueeDesigns = inkwellDesigns.filter((design) => getDesignPreviewUrl(design));
  const marqueeItems = marqueeDesigns.length > 0
    ? marqueeDesigns.map((design) => ({
        id: design.id,
        imageUrl: getDesignPreviewUrl(design),
        title: design.title || "Inkwell design",
      }))
    : [
        { id: "fallback-home", imageUrl: "/showcase-home.png", title: "Inkwell design" },
        { id: "fallback-chat", imageUrl: "/showcase-chat.png", title: "Inkwell design" },
        { id: "fallback-product", imageUrl: "/showcase-products.png", title: "Inkwell design" },
        { id: "fallback-editor", imageUrl: "/showcase-editor.png", title: "Inkwell design" },
      ];

  return (
    <div className="bp-page bp-splash-page page-enter">
      <SEOHead
        title="Quuil — Download The App"
        description="Create artwork, customize products, order securely, and discover community designs in the Quuil app."
        path="/"
        keywords={[
          "Quuil app",
          "custom merch app",
          "custom apparel app",
          "merch design app",
          "print on demand app",
        ]}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            name: "Quuil",
            applicationCategory: "DesignApplication",
            operatingSystem: "iOS",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            url: APP_STORE_URL,
          },
        ]}
      />

      <section className="bp-hero bp-splash-hero">
        <BlueprintGrid />
        <div className="bp-noise" aria-hidden="true" />
        <InteractiveBlueprintCanvas />
        <div className="bp-hero-border" aria-hidden="true">
          <CoordLabel className="bp-border-a1">A1</CoordLabel>
          <CoordLabel className="bp-border-a8">A8</CoordLabel>
          <CoordLabel className="bp-border-h1">H1</CoordLabel>
          <CoordLabel className="bp-border-h8">H8</CoordLabel>
        </div>

        <div className="bp-container bp-splash-grid px-5">
          <div className="bp-splash-copy">
            <span className="bp-kicker">Create // Customize // Order // Share</span>
            <img src={logoUrl} alt="Quuil" width={760} height={220} className="bp-hero-logo" />
            <div className="bp-app-icon-lockup" aria-label="Quuil app identity">
              <img src="/quuil-app-icon-showcase.png?v=5" alt="Quuil app icon" width={72} height={72} />
              <span>Create · Customize · Order</span>
            </div>
            <p className="bp-subtitle">
              Turn a photo, sketch, prompt, or Inkwell design into real merch. Preview it on products, check out securely, and track the order in the app.
            </p>
            <div className="bp-hero-actions bp-splash-actions">
              <BlueprintButton href={APP_STORE_URL}>Download on the App Store</BlueprintButton>
            </div>
            <div className="bp-splash-credit-badge" aria-label="Get 10 free credits when signing up">
              <span>Launch Offer</span>
              <strong>10 free credits</strong>
              <small>2 design sessions</small>
            </div>
          </div>

          <div className="bp-splash-phone-wrap" aria-label="Quuil app preview">
            <span className="bp-phone-glow bp-phone-glow-gold" aria-hidden="true" />
            <span className="bp-phone-glow bp-phone-glow-cyan" aria-hidden="true" />
            <BpFrame className="bp-phone-callout bp-phone-callout-top" label="APP PREVIEW">
              iOS
            </BpFrame>
            <QuuilPhoneMedia className="bp-phone-gif" />
          </div>
        </div>
      </section>

      <section className="bp-splash-inkwell" aria-labelledby="splash-inkwell-title">
        <BlueprintGrid light />
        <div className="bp-container">
          <div className="bp-splash-inkwell-header">
            <span className="bp-kicker">The Inkwell</span>
            <h2 id="splash-inkwell-title">See what others are making</h2>
          </div>
        </div>
        <div className="bp-splash-marquee" aria-label="Inkwell artwork previews">
          <div className="bp-splash-marquee-track">
            {[...marqueeItems, ...marqueeItems].map((item, index) => (
              <figure className="bp-splash-marquee-card" key={`${item.id}-${index}`}>
                <span className="bp-splash-marquee-image">
                  <img src={item.imageUrl} alt={item.title} loading={index < 4 ? "eager" : "lazy"} />
                </span>
                <figcaption>{item.title}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <div className="bp-splash-copyright" aria-label="Copyright">
        &copy; 2026 Quuil
      </div>
    </div>
  );
}
